import { Tooltip } from "antd";

interface StatusInfo {
  lastCompletedAction: string;
  currentPendingAction: string;
}

export const getViewPageChangeInfo = (step: string) => {
  switch (step) {
    case "Save Draft":
    case "Cancelled":
    case "Admin creates draft":
    case "Cancel offline DTS transfer":
      return { currentAction: "data-transfer" };
    case "Submitted":
      return { currentAction: "supplier-signature" };
    case "Approve Agreement":
      return { currentAction: "requester-signature" };
    case "Finalize Agreement":
    case "Security scan failed":
    case "Supplier Upload Save Draft":
    case "Upload offline DTS transfer":
    case "Supplier Upload Submitted (TEST)":
      return { currentAction: "data-upload" };
    case "Supplier Upload Submitted":
      return { currentAction: "review" };
    case "Supplier Upload Submitted (PROD)":
      return { currentAction: "review" };
    case "Reject Transfer":
      return { currentAction: "reject-transfer" };
    case "Finalize Transfer":
      return { currentAction: "finalize-transfer" };
    case "Initiate Offline DTS transfer":
      return { currentAction: "upload-offline-dts" };
    default:
      return { currentAction: "data-transfer" };
  }
};

export const getStatusInfo = (status: string, offlineFlow?: boolean): StatusInfo => {
  switch (status) {
    case "Save Draft":
      return {
        lastCompletedAction: "Draft Saved",
        currentPendingAction: "Resume DTS Agreement Creation",
      };
    case "Submitted":
      return {
        lastCompletedAction: "MSD Created DTS Agreement",
        currentPendingAction: "Pending Supplier Signature",
      };
    case "Approve Agreement":
      return {
        lastCompletedAction: "Supplier Signed DTS Agreement",
        currentPendingAction: "Pending MSD Signature",
      };
    case "Finalize Agreement":
    case "Supplier Upload Save Draft":
      return {
        lastCompletedAction: offlineFlow ? "MSD Uploaded Offline Signed DTS" : "MSD Signed DTS Agreement & eTMF Uploaded",
        currentPendingAction: "Pending Supplier Upload",
      };
    case "Supplier Upload Submitted (PROD)":
      return {
        lastCompletedAction: "Supplier Uploaded Data",
        currentPendingAction: "Pending MSD Upload Approval",
      };
    case "Supplier Upload Submitted (TEST)":
      return {
        lastCompletedAction: "Supplier Uploaded Data",
        currentPendingAction: "Pending Supplier Upload",
      };
    case "Finalize Transfer":
      return {
        lastCompletedAction:
          "MSD Approved Upload & Transfer Package Level Report Uploaded to eTMF",
        currentPendingAction: "Pending Supplier Upload",
      };
    case "Reject Transfer":
      return {
        lastCompletedAction: "MSD Rejected Upload",
        currentPendingAction: "Pending Supplier Re-Upload",
      };
    case "Cancelled":
      return {
        lastCompletedAction: "Cancelled",
        currentPendingAction: "Resume DTS Agreement Creation",
      };
    case "Security scan failed":
      return {
        lastCompletedAction: "Security scan failed in S3",
        currentPendingAction: "Pending Supplier Re-Upload",
      };
    case "Admin creates draft":
      return {
        lastCompletedAction: "Admin Initiated Offline DTS Draft",
        currentPendingAction: "Pending MSD Create New Offline DTS",
      };
    case "Initiate Offline DTS transfer":
      return {
        lastCompletedAction: "MSD Created Offline DTS",
        currentPendingAction: "Pending MSD Upload Offline Signed DTS",
      };
    case "Cancel offline DTS transfer":
      return {
        lastCompletedAction: "Cancelled",
        currentPendingAction: "Pending MSD Create New Offline DTS",
      };
    case "Upload offline DTS transfer":
      return {
        lastCompletedAction: "MSD Uploaded Offline Signed DTS",
        currentPendingAction: "Pending Supplier Upload",
      };
    default:
      // Optional: throw an error or log for debugging
      return {
        lastCompletedAction: "Unknown Status",
        currentPendingAction: "Unknown Status",
      };
  }
};

export const getUserProfile = () => {
  const profileDetailsString = sessionStorage.getItem("profile");
  if (profileDetailsString) {
    const user = JSON.parse(profileDetailsString);
    return user;
  }
  return null;
};

export const formatDateToISO = (dateString: string) => {
  const [day, monthName, year] = dateString.split("-");
  const date = new Date(`${monthName} ${day}, ${year}`);
  const month = date.toLocaleDateString("en-US", { month: "2-digit" });
  return `${year}-${month}-${day}`;
};

export const formatToLongDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.toLocaleDateString("en-US", { day: "2-digit" });
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.toLocaleDateString("en-US", { year: "numeric" });
  return `${day}-${month.charAt(0).toUpperCase() + month.slice(1)}-${year}`;
};

export const getUserRole = () => {
  const profileDetailsString = sessionStorage.getItem("profile");
  if (profileDetailsString) {
    const user = JSON.parse(profileDetailsString);
    return user.role;
  }
  return null;
};

export const isSupplierRole = () => {
  const userRole = getUserRole();
  if (userRole && userRole.toLowerCase().includes("supplier")) {
    return true;
  }
  return false;
};

export const isExternalSupplierRole = () => {
  const userRole = getUserRole();
  if (userRole && userRole.toLowerCase() === "supplier") {
    return true;
  }
  return false;
};

export const getUserIsid = () => {
  const profileDetailsString = sessionStorage.getItem("profile");
  if (profileDetailsString) {
    const user = JSON.parse(profileDetailsString);
    if (user) {
      return `${user.isid}`;
    }
  }
  return null;
};

export const wrappedText = (text: string | undefined, size: number) => {
  if (!text) {
    return ""; // Return an empty string if text is undefined or null
  }
  return (
    <Tooltip title={text} placement="right">
      {text.length > size ? text.slice(0, size) + "..." : text}
    </Tooltip>
  );
};

export const disableViewDetails = (action: string, isSupplierUser: boolean) => {
  if (
    isSupplierUser &&
    (
      action === "Pending MSD Upload Approval"
      || action === "Resume DTS Agreement Creation"
      || action === "Pending MSD Upload Offline Signed DTS"
      || action === "Pending MSD Create New Offline DTS"
    )) {
    return true;
  }
  return false;
};

export const disableDownloadDTSBtn = (action: string, isLoading: boolean) => {
  if (
    (action !== "Pending MSD Upload Approval" &&
      action !== "Pending Supplier Upload" && action !== "Pending Supplier Re-Upload") ||
    isLoading
  ) {
    return true;
  }
  return false;
};

export const displayFilename = (name: string) => {
  const regex = /^(.*?)\s-\s\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2} UTC\.\w+$/;
  const match = regex.exec(name);
  return match ? match[1] : name;
};

export const descriptionLabel = (isDataUpload: boolean) => {
  return (
    <span style={{ color: isDataUpload ? "grey" : "white", fontSize: "13px", lineHeight: 1.5 }}>
      Please include package name, relevance of milestone timing, data type and
      format including round of transfer, file total if applicable, and any
      other information significant to the delivery e.g.
      1234-321_Prod_005_20251231-1159 is the third of four IA2 raw clinical data
      transfers including 32 SAS files which will be used to create SDTM mapping
      for the planned second quarter of 2026 agency submission.
    </span>
  );
};

export const filesUploadLabel = () => {
  return (
    <span style={{ color: "grey", fontSize: "13px" }}>
      The zip file should not be password protected and no additional encryption
      should be applied. The zip file can contain any of the following files:
      Data set (.sas7bdat, .csv, .xls, etc.), Document (.doc, .xls, .xml, .pdf,
      .txt, etc.), Programs (.sas, .sql, etc.), Output (tables, listings, and
      figures - .rtf, .pdf, etc.)
    </span>
  );
};

// Function to parse the response
export const parseResponse = (response: any): Record<string, string> => {
  const userEmailMap: Record<string, string> = {};
  const users = response.data;
  users.forEach((user: any) => {
    userEmailMap[user.first_name] = user.email_id; // Use the renamed variable
  });

  return userEmailMap; // Return the renamed variable
};

export const formatRequesterName = (name: string): string => {
  const cleanedName = name.replace(/\[C\]/g, "").trim();
  const nameParts = cleanedName.split(",").map((part) => part.trim());
  if (nameParts.length >= 2) {
    return `${nameParts[1]} ${nameParts[0]}`;
  }
  return cleanedName;
};

export const eTMFWarningTextInitiatePage = () => {
  return (
    <span style={{ color: "black", fontSize: "15px" }}>
      The MSD Compound/Protocol that you selected does not have a folder
      existing in eTMF. If you continue with initiating the transfer, you will
      be responsible for uploading the signed DTS .pdf file into eTMF manually.
    </span>
  );
};

export const offlineDTSText = () => {
  return (
    <span style={{ color: "black", fontSize: "15px" }}>
      This transfer includes an offline DTS agreement. Please complete the information below to generate the offline DTS agreement.
    </span>
  );
};

export const offlineUploadDTSText = () => {
  return (
    <span style={{ color: "black", fontSize: "15px" }}>
      This transfer includes an offline DTS agreement. Please download the DTS agreement for offline signatures before re-uploading it.
    </span>
  );
};

export const eTMFWarningTextReviewPage = () => {
  return (
    <span style={{ color: "black", fontSize: "15px" }}>
      The MSD Compound/Protocol for this transfer setup does not have a folder
      existing in eTMF. If you continue with finalizing this data transfer, you
      will be responsible for manually uploading the associated transfer package
      level report into eTMF.
    </span>
  );
};

export const phdText = () => {
  return (
    <span>
      NOTE: In accordance with regulatory agency patient privacy regulation External Partner must confirm PHI data (e.g., patient initials, DOB, postal code, phone number, patient insurance information) are masked, de-identified, or redacted prior to sending data to our company.
    </span>
  );
};

export const unmaskingComponentText = () => {
  return (
    <span>
      NOTE: The DMTM should discuss the definition of unblinded data and unmasked data with the External Partner/Vendor and the implications of receiving unblinded or unmasked data.
      <br />If data are unblinded, describe how the unblinding occurred and how the unblinded data will be handled. If data are unmasked, then document the specific data elements in scope.
    </span>
  );
};
